import React, { useEffect, useState, useRef } from 'react';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.15
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} style={{
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(24px)',
    transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms`
  }} data-id="element-584">
      {children}
    </div>;
};
const Counter = ({
  end,
  duration = 1500,
  suffix = ''
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) => {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) {
        setStarted(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.4
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    let frameId: number;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * end));
      if (progress < 1) frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [started, end, duration]);
  return <span ref={ref} data-id="element-585">
      {value}
      {suffix}
    </span>;
};
import { useTranslation } from '../lib/i18n';
export function AboutPage() {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const stats = [{
    value: 250,
    suffix: en ? 'kg' : ru ? 'кг' : 'kg',
    label: en ? 'Max capacity' : ru ? 'Макс. грузоподъемность' : 'Maks. nosivost'
  }, {
    text: '4–8',
    label: en ? 'Weeks delivery' : ru ? 'Недель доставка' : 'Nedelja isporuka'
  }, {
    value: 1,
    suffix: '',
    label: en ? 'Year warranty' : ru ? 'Год гарантии' : 'Godina garancije'
  }, {
    value: 4,
    suffix: '',
    label: en ? 'Models available' : ru ? 'Доступных моделей' : 'Dostupna modela'
  }];
  return <main className="min-h-screen bg-dark-bg py-24" data-id="element-586">
      <div className="max-w-[700px] mx-auto px-5 sm:px-6" data-id="element-587">
        <FadeIn data-id="element-588">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-589">
            {en ? 'About us: ' : ru ? 'О нас: ' : 'O nama: '}
            <span className="text-brand-yellow" data-id="element-590">D-TECH</span>
          </h1>
          <div className="w-16 h-1 bg-brand-yellow rounded-full mb-12" data-id="element-591" />
        </FadeIn>

        <FadeIn delay={50} data-id="element-592">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16" data-id="element-593">
            {stats.map((stat, i) => <div key={i} className="bg-dark-surface border border-dark-border rounded-xl p-4 text-center hover:border-brand-yellow/60 transition-colors duration-300" data-id="element-594">
                <div className="text-3xl md:text-4xl font-black text-brand-yellow leading-none mb-2" data-id="element-595">
                  {'text' in stat ? stat.text : <Counter end={stat.value} suffix={stat.suffix} data-id="element-596" />}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wide" data-id="element-597">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </FadeIn>

        <div className="space-y-14 text-lg text-gray-300 leading-[1.8]" data-id="element-598">
          <FadeIn delay={100} data-id="element-599">
            <p data-id="element-600">
              {en ? <>
                  Transporting heavy cargo up stairs is the slowest and riskiest
                  part of any delivery.{' '}
                  <strong className="text-white" data-id="element-601">
                    D-TECH changes that workflow
                  </strong>{' '}
                  – we offer technology that allows a single operator to safely
                  and effortlessly transport loads up to{' '}
                  <span className="text-brand-yellow font-bold" data-id="element-602">250 kg</span>.
                </> : ru ? <>
                  Транспортировка тяжелых грузов по лестницам — самая медленная
                  и рискованная часть любой доставки.{' '}
                  <strong className="text-white" data-id="element-603">
                    D-TECH меняет этот рабочий процесс
                  </strong>{' '}
                  – мы предлагаем технологию, которая позволяет одному оператору
                  безопасно и без усилий перемещать грузы до{' '}
                  <span className="text-brand-yellow font-bold" data-id="element-604">250 кг</span>.
                </> : <>
                  Transport teškog tereta uz stepenice je najsporiji i
                  najrizičniji deo svake isporuke.{' '}
                  <strong className="text-white" data-id="element-605">
                    D-TECH menja taj način rada
                  </strong>{' '}
                  – nudimo tehnologiju koja omogućava da jedan operater sam,
                  bezbedno i bez napora, prenese teret do{' '}
                  <span className="text-brand-yellow font-bold" data-id="element-606">250 kg</span>.
                </>}
            </p>
          </FadeIn>

          <FadeIn delay={150} data-id="element-607">
            <p data-id="element-608">
              {en ? <>
                  Our machines guarantee stability on every step. One machine
                  significantly reduces the need for additional manpower, which
                  means <strong className="text-white" data-id="element-609">lower costs</strong>,
                  faster job completion, and{' '}
                  <strong className="text-white" data-id="element-610">zero damage to goods</strong>.
                </> : ru ? <>
                  Наши машины гарантируют стабильность на каждом шагу. Одна
                  машина значительно снижает потребность в дополнительной
                  рабочей силе, что означает{' '}
                  <strong className="text-white" data-id="element-611">снижение затрат</strong>, более
                  быстрое выполнение работы и{' '}
                  <strong className="text-white" data-id="element-612">
                    нулевое повреждение товаров
                  </strong>
                  .
                </> : <>
                  Naše mašine garantuju stabilnost na svakom stepeniku. Jedna
                  mašina znatno smanjuje potrebu za dodatnom radnom snagom, što
                  znači <strong className="text-white" data-id="element-613">manje troškova</strong>,
                  brže obavljen posao i{' '}
                  <strong className="text-white" data-id="element-614">nula oštećenja robe</strong>.
                </>}
            </p>
          </FadeIn>

          <FadeIn delay={200} data-id="element-615">
            <div className="border-l-2 border-brand-yellow pl-6 py-1" data-id="element-616">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-617">
                {en ? 'Direct import and best price' : ru ? 'Прямой импорт и лучшая цена' : 'Direktan uvoz i najbolja cena'}
              </h2>
              <p data-id="element-618">
                {en ? <>
                    We operate on a direct factory import model, without
                    middlemen and storage costs. The lead time is{' '}
                    <span className="text-brand-yellow font-bold" data-id="element-619">
                      4–8 weeks
                    </span>
                    , but it is an investment that pays off quickly.
                  </> : ru ? <>
                    Мы работаем по модели прямого импорта с завода, без
                    посредников и затрат на хранение. Срок поставки составляет{' '}
                    <span className="text-brand-yellow font-bold" data-id="element-620">
                      4–8 недель
                    </span>
                    , но это инвестиция, которая быстро окупается.
                  </> : <>
                    Radimo po modelu direktnog uvoza iz fabrike, bez posrednika
                    i troškova skladištenja. Rok isporuke je{' '}
                    <span className="text-brand-yellow font-bold" data-id="element-621">
                      4–8 nedelja
                    </span>
                    , ali to je investicija koja se brzo isplati.
                  </>}
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={250} data-id="element-622">
            <div className="border-l-2 border-brand-yellow pl-6 py-1" data-id="element-623">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-624">
                {en ? 'Service and warranty' : ru ? 'Сервис и гарантия' : 'Servis i garancija'}
              </h2>
              <p data-id="element-625">
                {en ? <>
                    All legal entities are provided with a{' '}
                    <span className="text-brand-yellow font-bold" data-id="element-626">
                      one-year
                    </span>{' '}
                    warranty. We cooperate with a network of trained service
                    technicians in Belgrade (Surčin) and Pančevo, ensuring{' '}
                    <strong className="text-white" data-id="element-627">original spare parts</strong>
                    , technical support, and practical training.
                  </> : ru ? <>
                    Всем юридическим лицам предоставляется гарантия на{' '}
                    <span className="text-brand-yellow font-bold" data-id="element-628">
                      один год
                    </span>
                    . Мы сотрудничаем с сетью обученных сервисных специалистов в
                    Белграде (Сурчин) и Панчево, обеспечивая{' '}
                    <strong className="text-white" data-id="element-629">
                      оригинальные запасные части
                    </strong>
                    , техническую поддержку и практическое обучение.
                  </> : <>
                    Za sva pravna lica obezbeđena je garancija od{' '}
                    <span className="text-brand-yellow font-bold" data-id="element-630">
                      godinu dana
                    </span>
                    . Sarađujemo sa mrežom obučenih servisera u Beogradu
                    (Surčin) i Pančevu, čime obezbeđujemo{' '}
                    <strong className="text-white" data-id="element-631">
                      originalne rezervne delove
                    </strong>
                    , tehničku podršku i praktičnu obuku.
                  </>}
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </main>;
}